<mat-expansion-panel
  class="!overflow-hidden !rounded-2xl !border !border-gray-6 !bg-surface-default !shadow-sm transition-all duration-200 hover:!border-gray-7 !before:hidden relative"
  [expanded]="expanded()"
  (expandedChange)="expanded.set($event)"
>
  <!-- Subtle decorative ambient glow -->
  <div
    class="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-blue-a2 blur-3xl"
  ></div>

  <!-- Card Header -->
  <mat-expansion-panel-header
    class="!h-auto !py-4.5 !px-6 hover:!bg-gray-2/40 transition-colors [&_.mat-content]:!flex-row [&_.mat-content]:!items-center [&_.mat-content]:!justify-between [&_.mat-content]:!w-full [&_.mat-content]:!m-0 [&_.mat-content]:!overflow-visible"
  >
    <div class="flex items-center justify-between w-full gap-4 mr-3">
      <div class="flex items-center gap-3.5 min-w-0">
        <div
          class="h-11 w-11 flex items-center justify-center rounded-xl bg-blue-a2 text-blue-11 border border-blue-a4 shadow-xs shrink-0"
        >
          <mat-icon class="text-2xl leading-none">volume_up</mat-icon>
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h4 class="text-base sm:text-lg font-bold text-gray-12 m-0">
              Environnement sonore et acoustique
            </h4>
          </div>
          <p class="text-xs sm:text-sm text-gray-11 m-0 mt-0.5 truncate hidden sm:block">
            Niveau de bruit modélisé en façade et exposition aux nuisances sonores.
          </p>
        </div>
      </div>

      <div class="shrink-0">
        @if (categoryInfo(); as cat) {
          <div
            class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold px-3.5 py-1.5 rounded-full border shadow-xs"
            [class]="cat.classes"
          >
            <span>{{ cat.label }}</span>
          </div>
        } @else {
          <div
            class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium px-3 py-1 rounded-full border border-gray-6 bg-gray-2 text-gray-11 shadow-2xs"
          >
            <mat-icon fontIcon="hourglass_empty" class="text-xs leading-none shrink-0"></mat-icon>
            <span>Analyse en cours</span>
          </div>
        }
      </div>
    </div>
  </mat-expansion-panel-header>

  <div class="px-6 pb-6 pt-3 border-t border-gray-5">
    @if (noise(); as data) {
      <div class="relative mt-5 grid grid-cols-1 gap-5 md:grid-cols-12">
        <!-- Left: dB Readout Hero Card -->
        <div
          class="md:col-span-4 rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between backdrop-blur-xs"
        >
          <div>
            <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-1">
              <span>Niveau sonore estimé</span>
              <mat-icon fontIcon="graphic_eq" class="text-sm leading-none text-gray-9"></mat-icon>
            </div>
            <div class="mt-2 flex items-baseline gap-2">
              <span class="text-4xl font-black tracking-tight text-gray-12">
                {{ data.levelDb ? data.levelDb : '—' }}
              </span>
              @if (data.levelDb) {
                <span class="text-base font-bold text-gray-11">dB(A)</span>
              }
            </div>
          </div>

          <div class="mt-4 pt-3 border-t border-gray-5">
            <p class="text-xs sm:text-sm text-gray-11 m-0 leading-relaxed">
              {{ categoryInfo()?.description || 'Indice moyen Lden mesuré en décibels pondérés A.' }}
            </p>
          </div>
        </div>

        <!-- Right: Sleek Gauge, Scale & Sources -->
        <div
          class="md:col-span-8 rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between gap-5 backdrop-blur-xs"
        >
          <!-- Visual Gauge Bar -->
          <div>
            <div class="flex items-center justify-between text-sm text-gray-11 mb-2.5 font-medium">
              <span class="font-semibold text-gray-12">Intensité sonore modélisée</span>
              <span
                class="text-xs sm:text-sm font-bold text-gray-12 bg-surface-default px-3 py-1 rounded-full border border-gray-5 shadow-2xs"
              >
                {{ data.levelDb ? data.levelDb + ' dB(A)' : 'Non disponible' }}
              </span>
            </div>

            <!-- Multi-stop Gradient Track -->
            <div class="relative w-full py-1">
              <div
                class="w-full h-3.5 rounded-full shadow-inner border border-gray-5"
                style="
                  background: linear-gradient(to right, #10b981 0%, #34d399 30%, #f59e0b 60%, #ef4444 100%);
                "
              ></div>

              <!-- Position Indicator Needle -->
              <div
                class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 transition-all duration-500 ease-out"
                [style.left.%]="gaugePercent()"
              >
                <div
                  class="h-6 w-3.5 rounded-full bg-gray-12 shadow-md border-2 border-white ring-2 ring-gray-4 flex items-center justify-center"
                >
                  <div class="w-0.5 h-2.5 bg-white rounded-full"></div>
                </div>
              </div>
            </div>

            <!-- Reference Ticks / Legend -->
            <div class="flex items-center justify-between text-xs sm:text-sm text-gray-11 mt-2 px-1">
              <span class="text-jade-11 font-semibold flex items-center gap-1">
                <span>Calme</span>
                <span class="text-xs text-gray-10 font-normal">(&lt; 50 dB)</span>
              </span>
              <span class="text-amber-11 font-semibold flex items-center gap-1">
                <span>Modéré</span>
                <span class="text-xs text-gray-10 font-normal">(50 - 65 dB)</span>
              </span>
              <span class="text-ruby-11 font-semibold flex items-center gap-1">
                <span>Exposé</span>
                <span class="text-xs text-gray-10 font-normal">(&gt; 65 dB)</span>
              </span>
            </div>

            <!-- Real-world Benchmark Analogy -->
            <div
              class="mt-3.5 rounded-lg bg-surface-default/80 p-3 border border-gray-5 flex items-center gap-2.5 text-sm text-gray-11"
            >
              <mat-icon fontIcon="hearing" class="text-blue-11 text-lg leading-none shrink-0"></mat-icon>
              <span class="text-xs sm:text-sm leading-relaxed">
                <strong class="text-gray-12 font-semibold">Repère : </strong>
                {{ soundEquivalence() }}.
              </span>
            </div>
          </div>

          <!-- Sources de bruit -->
          <div class="border-t border-gray-5 pt-3.5">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-semibold text-gray-12"
                >Infrastructures et sources de bruit à proximité</span
              >
              @if (data.mainSources && data.mainSources.length > 0) {
                <span class="text-xs text-gray-11 font-medium">
                  {{ data.mainSources.length }} source{{
                    data.mainSources.length > 1 ? 's' : ''
                  }}
                  identifiée{{ data.mainSources.length > 1 ? 's' : '' }}
                </span>
              }
            </div>

            @if (data.mainSources && data.mainSources.length > 0) {
              <div class="flex flex-wrap gap-2">
                @for (source of data.mainSources; track source) {
                  <span
                    class="inline-flex items-center gap-1.5 rounded-lg border border-gray-5 bg-surface-default px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-12 shadow-2xs"
                  >
                    <mat-icon
                      [fontIcon]="getSourceIcon(source)"
                      class="text-base leading-none text-indigo-11"
                    ></mat-icon>
                    <span>{{ source | noiseSourceDisplay }}</span>
                  </span>
                }
              </div>
            } @else {
              <p class="text-sm text-gray-11 m-0 italic flex items-center gap-1.5">
                <mat-icon fontIcon="check" class="text-jade-11 text-sm leading-none"></mat-icon>
                <span
                  >Aucune infrastructure sonore majeure recensée à proximité immédiate (autoroute, voie ferrée
                  ou couloir aérien).</span
                >
              </p>
            }
          </div>
        </div>
      </div>

      <!-- Micro footer note -->
      <div class="mt-4 flex items-center gap-2 text-xs text-gray-11 pt-2">
        <mat-icon fontIcon="info_outline" class="text-sm text-blue-11 leading-none shrink-0"></mat-icon>
        <span
          >Évaluation acoustique basée sur les cartes stratégiques de bruit du Cerema et de la directive
          européenne 2002/49/CE.</span
        >
      </div>
    } @else {
      <!-- Empty / Pending State -->
      <div
        class="mt-4 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-6 py-8 text-center bg-gray-2/50"
      >
        <div class="h-10 w-10 rounded-full bg-gray-3 flex items-center justify-center text-gray-10">
          <mat-icon class="text-xl">sync_disabled</mat-icon>
        </div>
        <div>
          <p class="text-sm font-semibold text-gray-12 m-0">Données acoustiques en cours de chargement</p>
          <p class="text-sm text-gray-11 m-0 mt-1 max-w-sm">
            Le niveau sonore et les cartes de bruit stratégiques du secteur sont en cours d'analyse.
          </p>
        </div>
      </div>
    }
  </div>
</mat-expansion-panel>
